import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { captureException } from '@/lib/sentry';

// A transient chunk-load failure: right after a new deploy the live index.html
// can reference a JS chunk hash that the CDN hasn't served yet, so a lazy import
// rejects. These are recoverable by simply reloading — they are NOT app bugs.
function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const m = String(error.message || '');
  return /Loading chunk \d+ failed|Loading CSS chunk|dynamically imported module|Failed to fetch dynamically imported/i.test(m);
}

/**
 * Per-route error boundary — isolates module failures.
 * If the Marketing module crashes, only Marketing shows this error.
 * Cases, Dashboard, and all other modules continue working normally.
 */
export default class ModuleErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Transient chunk-load failure (usually the deploy window) → auto-reload
    // ONCE to pick up the fresh chunks, instead of showing the crash card.
    // Guarded via sessionStorage so a genuinely-broken chunk can't reload-loop:
    // if we already reloaded in the last 10s, fall through to the card.
    if (isChunkLoadError(error)) {
      try {
        const KEY = '__chunk_reload_at';
        const last = Number(sessionStorage.getItem(KEY) || 0);
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage unavailable — fall through to the card */ }
    }

    // Sentry first — captures the readable stack regardless of whether the
    // backend log call below succeeds.
    captureException(error, {
      boundary: 'module',
      module:   this.props.moduleName || 'unknown',
      componentStack: info?.componentStack?.slice(0, 500),
    });

    // Log to our backend error system
    const token = localStorage.getItem('token');
    if (token) {
      const slug = window.location.hostname.split('.')[0];
      // Router is mounted at /api/platform/observability — the old
      // /api/observability path 404'd silently on EVERY environment, so no
      // frontend crash report ever landed (caught 2026-07-10 while chasing
      // the client upload failures).
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/observability/frontend-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Slug': slug,
        },
        body: JSON.stringify({
          error_type:  error.name,
          message:     error.message,
          stack:       error.stack?.slice(0, 2000),
          component:   info.componentStack?.slice(0, 1000),
          url:         window.location.href,
          module:      this.props.moduleName || 'unknown',
        }),
      }).catch(() => {}); // never let this crash anything
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="dashboard-layout">
        {/* Sidebar still renders — only this module is affected */}
        <div className="main-content flex items-center justify-center min-h-screen bg-gray-50">
          <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-10 max-w-md text-center">
            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {this.props.moduleName || 'This module'} encountered an error
            </h2>
            <p className="text-slate-500 text-sm mb-6 leading-relaxed">
              The rest of the platform is unaffected. Our team has been automatically notified.
              Try refreshing this page to continue.
            </p>
            {process.env.NODE_ENV === 'development' && (
              <pre className="text-left text-xs bg-slate-50 p-4 rounded-xl text-red-600 mb-6 overflow-auto max-h-40">
                {this.state.error?.message}
              </pre>
            )}
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors"
              >
                <RefreshCw size={14} /> Try Again
              </button>
              <button
                onClick={() => {
                  // Navigate to the role-appropriate dashboard, not hardcoded /admin
                  try {
                    const u = JSON.parse(localStorage.getItem('user') || '{}');
                    const roleMap = {
                      super_admin: '/admin', admin: '/admin', process_manager: '/admin',
                      case_officer: '/officer', accountant: '/accountant',
                      sales_consultant: '/sales', sales_manager: '/sales/manager',
                      marketing_manager: '/admin/marketing', legal_counsel: '/legal',
                      client: '/client',
                    };
                    window.location.href = roleMap[u.role] || '/admin';
                  } catch {
                    window.location.href = '/login';
                  }
                }}
                className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
